import { Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

import { getPropsFrom, upsertChildren, xmlElementToString } from '../../../../utils/xmlParser';

interface CommentItemProperties {
  text: string;
  style: string;
  userName: string;
  relatedItemId: string;
  addedWhileProcessing: string;
}

@Component({
  selector: 'lfe-comment-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './comment-item-form.component.html',
  standalone: true,
  styleUrl: './comment-item-form.component.css'
})
export class CommentItemFormComponent extends InteractiveItemComponent<CommentItemProperties> implements OnInit {
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & CommentItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: 'Normal',
    userName: '',
    relatedItemId: '',
    addedWhileProcessing: 'False'
  };
  disabled = false;

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `comment_${this.currentId()}`);

  userNameTrimmed = computed(() => this.properties.userName.trim());
  addedWhileProcessing = computed(() => this.properties.addedWhileProcessing === 'True');

  hasInteracted = signal(false);

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = this.properties.text || '';
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & CommentItemProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'] || '';
    const id = props['ID'] || '';
    const text = props['Text'] || '';
    const style = props['Style'] || 'Normal';
    const userName = props['UserName'] || '';
    const addedWhileProcessing = props['AddedWhileProcessing'] || 'False';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';
    const relatedItemId = props['RelatedItemId'] || '';

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      relatedItemId,
      resultValue,
      resultUser,
      resultTime,
      text,
      style,
      userName,
      addedWhileProcessing,
      isGenericRepeatItem
    };
  }

  onCommentFocus(event: Event): void {
    this.onCommentChange(event);
    this.hasInteracted.set(true);
  }

  onCommentChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    this.formControl?.setValue(value);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.text = value;
    this.properties.resultValue = value;
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.hasInteracted()) {
      upsertChildren(element, {
        Text: this.properties.text
      });

      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }
}
